import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import { meetsMinimumVersion } from '@/apps/environments';
import { isWebBasedApp, isMobileApp } from '@/utils/apps';
import semver from 'semver';
import { SdkTools } from '@pendo/services/BuildingBlocks';
import { getPlatformLabel } from '@/common/apps/apps.utils';
import { i18n_t } from '@utils/i18n';
import { getActiveApplicationsMap } from '@/data/session/session-globals.ts';
import { MOBILE_PLATFORM_IDS } from '@pendo/middleware';

const { MIN_ACTIVE_SDK_VERSION } = SdkTools || {};
const $t = i18n_t;

export const legacyAppId = -323232;

export const getAllApps = () => ({
    id: '*',
    displayName: $t('globalFilters.All Apps'),
});

// allApps IS DEPRECATED
// This is just for backwards compatibility.
// In order to leverage i18n, please replace allApps with getAllApps()
export const allApps = getAllApps();

// for use with pendo components lib
export function getAppIcon (app) {
    switch (app?.platform) {
        case 'web':
            return { type: 'monitor' };
        case 'extension':
            return { type: 'puzzle-piece' };
        case 'ios':
            return { type: 'ios' };
        case 'android':
            return { type: 'android' };
    }

    return undefined;
}

export function getI18nAppPlatformLabel (platform) {
    switch (platform) {
        case 'ios':
            return $t('common.iOS');
        case 'android':
            return $t('common.Android');
        case 'mobile':
            return $t('common.Mobile');
        case 'web':
            return $t('common.Web');
        case 'extension':
            return $t('common.Extension');
        case 'unknown':
            return $t('common.Unknown');
        default:
            return platform;
    }
}

// This is the base class containing Apps related service methods.
// It is meant to be framework agnostic so the core functionality can
// be used in both Angular and Vue. An instance of this class is exported
// so it can be used in Vue code. It is also extended (see below)
// by another class that is then registered as an Angular service so it
// can be used in Angular code.
class Apps {
    constructor () {
        Object.assign(this, {
            ALL: 'all',
            ALL_PLATFORM: '*',
            MOBILE_PLATFORMS: ['ios', 'android'],
            MOBILE_PLATFORM: 'mobile',
            IOS_PLATFORM: 'ios',
            ANDROID_PLATFORM: 'android',
            WEB_PLATFORM: 'web',
            EXTENSION_PLATFORM: 'extension',
            WEB_LIKE_PLATFORMS: 'web|extension',
            ALL_PLATFORM_LABEL: 'All Apps',
            MOBILE_PLATFORM_LABEL: 'Mobile',
            IOS_PLATFORM_LABEL: 'iOS',
            ANDROID_PLATFORM_LABEL: 'Android',
            WEB_PLATFORM_LABEL: 'Web',
            EXTENSION_PLATFORM_LABEL: 'Extension',
            UNKNOWN_PLATFORM_LABEL: 'Unknown',
            IOS_AGG_ID: 'expandAppIds("ios")',
            ANDROID_AGG_ID: 'expandAppIds("android")',
            WEB_AGG_ID: 'expandAppIds("web")',
            WEB_LIKE_AGG_ID: 'expandAppIds("web|extension")',
            REPLAY_WEB_AGG_ID: 'expandAppIds("replay-web")',
            REPLAY_MOBILE_AGG_ID: 'expandAppIds("replay-mobile")',
            REPLAY_EXTENSION_AGG_ID: 'expandAppIds("replay-extension")',
            REPLAY_LIKE_AGG_ID: 'expandAppIds("replay-web|replay-extension")',
            REPLAY_ALL_AGG_ID: 'expandAppIds("replay-all")',
            ALL_AGG_ID: 'expandAppIds("*")',
            MOBILE_AGG_ID: 'expandAppIds("mobile")',
            EXTENSION_AGG_ID: 'expandAppIds("extension")',
            RETRO_MOBILE_AGG_ID: 'expandAppIds("mobile-retro")',
            RETRO_ANALYTICS_AGG_ID: 'expandAppIds("web|mobile-retro|extension")',
        });

        // This maps to the ID used for the web app automatically created via APP-4857
        // This can and should be removed after the Insert migration is complete
        this.legacyAppId = legacyAppId;
    }

    isV2MobileApp (app) {
        return isMobileApp(app.platform) && get(app, 'applicationFlags.mobileRetroactiveAnalyticsApp', false);
    }

    isRetroAnalyticsApp (app) {
        return isWebBasedApp(app.platform) || this.isV2MobileApp(app);
    }

    isPartnerAdoptApplication (app) {
        return !!app.applicationFlags?.partnerAdoptApplication;
    }

    checkForMultipleApps (apps, options = {}) {
        if (!isEmpty(options.hasMultipleAppsPlatformType)) {
            const platforms = [].concat(options.hasMultipleAppsPlatformType);
            apps = apps.filter((app) => platforms.includes(app.platform));
        }

        return !!apps.length && apps.length > 1;
    }

    getIntegratedTag (app) {
        if (app.integrated) {
            return {
                label: isWebBasedApp(app.platform) ? 'Installed' : 'Integrated',
                type: 'success',
                size: 'small',
            };
        }

        return {
            label: isWebBasedApp(app.platform) ? 'Not installed' : 'Not integrated',
            type: 'info',
            size: 'small',
        };
    }

    getAppStatusTags (app) {
        if (this.isDeactivatedApp(app)) {
            return [
                {
                    label: 'Deactivated',
                    type: 'error',
                    size: 'small',
                },
            ];
        }

        const integratedTag = this.getIntegratedTag(app);

        const extensionAppIsDisabled =
            app.platform === this.EXTENSION_PLATFORM && app.applicationFlags?.disableExtensionInjection;
        const webOrMobileAppIsDisabled =
            (app.platform === this.WEB_PLATFORM || isMobileApp(app.platform)) && app.applicationFlags?.disablePendo;
        const isDisabled = extensionAppIsDisabled || webOrMobileAppIsDisabled;

        if (isDisabled) {
            const disabledTag = {
                label: 'Disabled',
                type: 'warning',
                size: 'small',
            };

            return [integratedTag, disabledTag];
        }

        return [integratedTag];
    }

    getPlatformLabel (app) {
        return getPlatformLabel(app);
    }

    getAppIconLabel (platform) {
        switch (platform) {
            case this.IOS_PLATFORM:
                return 'fa-apple';
            case this.ANDROID_PLATFORM:
                return 'fa-android';
            case this.EXTENSION_PLATFORM:
                return 'fa-puzzle-piece';
            default:
                return 'fa-desktop';
        }
    }

    getAppIconUnicode (platform) {
        switch (platform) {
            case this.IOS_PLATFORM:
                return '\uf179';
            case this.ANDROID_PLATFORM:
                return '\uf17b';
            case this.WEB_PLATFORM:
                return '\uf108';
            case this.EXTENSION_PLATFORM:
                return '\uf12e';
            default:
                return '';
        }
    }

    getAppIconSvg (platform) {
        switch (platform) {
            case this.IOS_PLATFORM:
                return '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="ios"><path d="M17.7516 12.3813C17.7422 10.661 18.5203 9.36257 20.0953 8.40632C19.2141 7.14539 17.8828 6.45164 16.125 6.3157C14.4609 6.18445 12.6422 7.28601 11.9766 7.28601C11.2734 7.28601 9.66094 6.36257 8.39531 6.36257C5.77969 6.40476 3 8.44851 3 12.6063C3 13.8344 3.225 15.1032 3.675 16.4126C4.275 18.1329 6.44062 22.3516 8.7 22.2813C9.88125 22.2532 10.7156 21.4423 12.2531 21.4423C13.7437 21.4423 14.5172 22.2813 15.8344 22.2813C18.1125 22.2485 20.0719 18.4141 20.6437 16.6891C17.5875 15.2501 17.7516 12.4704 17.7516 12.3813V12.3813ZM15.0984 4.68445C16.3781 3.1657 16.2609 1.78289 16.2234 1.28601C15.0938 1.35164 13.7859 2.05476 13.0406 2.92195C12.2203 3.85007 11.7375 4.99851 11.8406 6.29226C13.0641 6.38601 14.1797 5.75789 15.0984 4.68445V4.68445Z" stroke="none" fill="currentColor"></path></svg>';
            case this.ANDROID_PLATFORM:
                return '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="android"><path d="M5.7 10.0893V15.5174C5.7 16.2393 5.13281 16.8158 4.41094 16.8158C3.69375 16.8158 3 16.2346 3 15.5174V10.0893C3 9.38144 3.69375 8.80019 4.41094 8.80019C5.11875 8.80019 5.7 9.38144 5.7 10.0893ZM6.20625 17.4486C6.20625 18.2174 6.825 18.8361 7.59375 18.8361H8.52656L8.54062 21.7002C8.54062 23.4299 11.1281 23.4158 11.1281 21.7002V18.8361H12.8719V21.7002C12.8719 23.4205 15.4734 23.4252 15.4734 21.7002V18.8361H16.4203C17.1797 18.8361 17.7984 18.2174 17.7984 17.4486V9.03926H6.20625V17.4486V17.4486ZM17.8312 8.58457H6.15469C6.15469 6.57832 7.35469 4.83457 9.13594 3.92519L8.24063 2.27051C8.10938 2.04082 8.44219 1.89551 8.55469 2.09238L9.46406 3.76113C11.1 3.03457 12.9797 3.07207 14.5406 3.76113L15.4453 2.09707C15.5625 1.89551 15.8906 2.04551 15.7594 2.27519L14.8641 3.92519C16.6313 4.83457 17.8312 6.57832 17.8312 8.58457V8.58457ZM9.82969 5.98301C9.82969 5.71582 9.61406 5.49082 9.3375 5.49082C9.07031 5.49082 8.85938 5.71582 8.85938 5.98301C8.85938 6.25019 9.075 6.47519 9.3375 6.47519C9.61406 6.47519 9.82969 6.25019 9.82969 5.98301ZM15.1453 5.98301C15.1453 5.71582 14.9297 5.49082 14.6672 5.49082C14.3906 5.49082 14.175 5.71582 14.175 5.98301C14.175 6.25019 14.3906 6.47519 14.6672 6.47519C14.9297 6.47519 15.1453 6.25019 15.1453 5.98301ZM19.5891 8.80019C18.8812 8.80019 18.3 9.36738 18.3 10.0893V15.5174C18.3 16.2393 18.8812 16.8158 19.5891 16.8158C20.3109 16.8158 21 16.2346 21 15.5174V10.0893C21 9.36738 20.3062 8.80019 19.5891 8.80019Z" stroke="none" fill="currentColor"></path></svg>';
            case this.WEB_PLATFORM:
                return '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>';
            case this.EXTENSION_PLATFORM:
                return '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 15.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z"/></svg>';
            default:
                return '';
        }
    }

    getAppFiltersFromOption (optionId) {
        let appFilters = [];
        // send a specific appId to the agg
        if (optionId !== 'all' && optionId !== '*') {
            appFilters = [optionId];
        }
        // only have the agg deal with mobile data
        if (optionId === 'mobile') {
            appFilters = [this.IOS_PLATFORM, this.ANDROID_PLATFORM];
        }

        return appFilters;
    }

    expandAppIds (appId, apps) {
        const appIds = isArray(appId) ? appId : [appId];

        return flatMap(
            appIds.map((id) => {
                switch (id) {
                    case this.ALL_AGG_ID:
                    case this.REPLAY_ALL_AGG_ID:
                    case this.ALL:
                    case this.ALL_PLATFORM:
                        return apps.map((app) => app.id);
                    case this.IOS_AGG_ID:
                    case this.IOS_PLATFORM:
                        return apps.filter((app) => app.platform === this.IOS_PLATFORM).map((app) => app.id);
                    case this.ANDROID_AGG_ID:
                    case this.ANDROID_PLATFORM:
                        return apps.filter((app) => app.platform === this.ANDROID_PLATFORM).map((app) => app.id);
                    case this.REPLAY_MOBILE_AGG_ID:
                    case this.MOBILE_AGG_ID:
                    case this.MOBILE_PLATFORM:
                        return apps.filter((app) => this.MOBILE_PLATFORMS.includes(app.platform)).map((app) => app.id);
                    case this.REPLAY_WEB_AGG_ID:
                    case this.WEB_AGG_ID:
                    case this.WEB_PLATFORM:
                        return apps.filter((app) => app.platform === this.WEB_PLATFORM).map((app) => app.id);
                    case this.REPLAY_EXTENSION_AGG_ID:
                    case this.EXTENSION_AGG_ID:
                    case this.EXTENSION_PLATFORM:
                        return apps.filter((app) => app.platform === this.EXTENSION_PLATFORM).map((app) => app.id);
                    case this.REPLAY_LIKE_AGG_ID:
                    case this.WEB_LIKE_AGG_ID:
                        return apps
                            .filter((app) => [this.WEB_PLATFORM, this.EXTENSION_PLATFORM].includes(app.platform))
                            .map((app) => app.id);
                    default:
                        return id;
                }
            }),
        );
    }

    getAppAggIdsFromOption (optionId) {
        switch (optionId) {
            case 'all':
                return this.ALL_AGG_ID;
            case 'mobile':
            case '*':
            case 'ios':
            case 'android':
            case 'web':
            case 'extension':
                return `expandAppIds("${optionId}")`;
            case this.WEB_LIKE_PLATFORMS:
                return this.WEB_LIKE_AGG_ID;
            default:
                return optionId;
        }
    }

    getReplayAppAggIdsFromOption (optionId) {
        // all Replay apps have either a Web or Extension platform
        // but we want to pass in replay-specific agg ids for agg-performance
        // so that the BE is only searching through apps that have ever had Replay enabled
        switch (optionId) {
            case this.MOBILE_PLATFORM:
                return this.REPLAY_MOBILE_AGG_ID;
            case this.WEB_PLATFORM:
            case this.WEB_AGG_ID:
                return this.REPLAY_WEB_AGG_ID;
            case this.EXTENSION_PLATFORM:
            case this.EXTENSION_AGG_ID:
                return this.REPLAY_EXTENSION_AGG_ID;
            case this.WEB_LIKE_PLATFORMS:
            case this.WEB_LIKE_AGG_ID:
                return this.REPLAY_LIKE_AGG_ID;
            case this.ALL:
            case this.ALL_PLATFORM:
            case this.ALL_AGG_ID:
                return this.REPLAY_ALL_AGG_ID;
            default:
                return optionId;
        }
    }

    getOptionIdFromAggId (aggId) {
        switch (aggId) {
            case this.ALL_AGG_ID:
                return '*';
            case this.WEB_AGG_ID:
                return 'web';
            case this.IOS_AGG_ID:
            case this.ANDROID_AGG_ID:
            case this.MOBILE_AGG_ID:
                return 'mobile';
            case this.EXTENSION_AGG_ID:
                return 'extension';
            default:
                return aggId;
        }
    }

    getAppGroupLabelByAggId (aggId) {
        if (!aggId || Number.isInteger(aggId)) return;
        if (Array.isArray(aggId)) {
            if (aggId.length === 0) return;
            if (aggId.every((id) => MOBILE_PLATFORM_IDS.includes(id))) {
                return 'Mobile Apps';
            }

            return;
        }

        switch (aggId) {
            case this.ALL:
            case this.ALL_AGG_ID:
            case this.ALL_PLATFORM:
                return $t('globalFilters.All Apps');
            case this.WEB_AGG_ID:
            case this.WEB_PLATFORM:
                return $t('common.Web Apps');
            case this.IOS_AGG_ID:
            case this.IOS_PLATFORM:
            case this.ANDROID_AGG_ID:
            case this.ANDROID_PLATFORM:
            case this.MOBILE_AGG_ID:
            case this.MOBILE_PLATFORM:
                return $t('common.Mobile Apps');
            case this.EXTENSION_AGG_ID:
            case this.EXTENSION_PLATFORM:
                return $t('common.Extension Apps');
            case this.WEB_LIKE_PLATFORMS:
            case this.WEB_LIKE_AGG_ID:
                return $t('common.Web & Extension Apps');
        }
    }

    getAppGroupLabelBySelection (allApps, selectedApps) {
        const appCounts = {
            [this.WEB_PLATFORM]: { total: 0, remaining: 0 },
            [this.IOS_PLATFORM]: { total: 0, remaining: 0 },
            [this.ANDROID_PLATFORM]: { total: 0, remaining: 0 },
            [this.EXTENSION_PLATFORM]: { total: 0, remaining: 0 },
        };

        for (const { platform } of allApps) {
            if (appCounts[platform]) {
                appCounts[platform].total++;
            }
        }

        for (const counts of Object.values(appCounts)) {
            counts.remaining = counts.total;
        }

        for (const { platform } of selectedApps) {
            if (appCounts[platform]) {
                appCounts[platform].remaining--;
            }
        }

        return this.getPlatformShorthand(appCounts, selectedApps.length);
    }

    getPlatformShorthand (appCounts, numOfAppsSelected) {
        const platformsSelected = [];
        let hasUnselected = false;
        for (const [platform, counts] of Object.entries(appCounts)) {
            if (counts.total !== counts.remaining && counts.remaining !== 0) {
                return $t('common.Apps ({numOfAppsSelected})', { numOfAppsSelected });
            }

            if (counts.total !== 0 && counts.remaining === 0) {
                platformsSelected.push(platform);
            }

            hasUnselected ||= counts.remaining;
        }

        if (!hasUnselected) {
            return $t('common.All apps ({numOfAppsSelected})', { numOfAppsSelected });
        }

        if (platformsSelected.length === 1) {
            switch (platformsSelected[0]) {
                case this.WEB_PLATFORM:
                    return $t('common.Web apps ({numOfAppsSelected})', { numOfAppsSelected });
                case this.EXTENSION_PLATFORM:
                    return $t('common.Extension apps ({numOfAppsSelected})', { numOfAppsSelected });
            }
        }

        if (platformsSelected.length === 2) {
            if (platformsSelected.includes(this.IOS_PLATFORM) && platformsSelected.includes(this.ANDROID_PLATFORM)) {
                return $t('common.Mobile apps ({numOfAppsSelected})', { numOfAppsSelected });
            }

            if (platformsSelected.includes(this.WEB_PLATFORM) && platformsSelected.includes(this.EXTENSION_PLATFORM)) {
                return $t('common.Web and extension apps ({numOfAppsSelected})', { numOfAppsSelected });
            }
        }

        return $t('common.Apps ({numOfAppsSelected})', { numOfAppsSelected });
    }

    getSelectPlatformLabel (aggId) {
        switch (aggId) {
            case this.ALL:
            case this.ALL_AGG_ID:
            case this.ALL_PLATFORM:
                return $t('common.Select all apps');
            case this.WEB_AGG_ID:
            case this.WEB_PLATFORM:
                return $t('common.Select web apps');
            case this.MOBILE_AGG_ID:
            case this.MOBILE_PLATFORM:
            case this.IOS_AGG_ID:
            case this.IOS_PLATFORM:
            case this.ANDROID_AGG_ID:
            case this.ANDROID_PLATFORM:
                return $t('common.Select mobile apps');
            case this.EXTENSION_AGG_ID:
            case this.EXTENSION_PLATFORM:
                return $t('common.Select extension apps');
            case this.WEB_LIKE_PLATFORMS:
            case this.WEB_LIKE_AGG_ID:
                return $t('common.Select web & extension apps');
        }
    }

    sortApps (apps) {
        return [
            ...apps.filter((app) => app.platform === this.WEB_PLATFORM).sort(this.displayNameSorter),
            ...apps.filter((app) => app.platform === this.IOS_PLATFORM).sort(this.displayNameSorter),
            ...apps.filter((app) => app.platform === this.ANDROID_PLATFORM).sort(this.displayNameSorter),
            ...apps.filter((app) => app.platform === this.EXTENSION_PLATFORM).sort(this.displayNameSorter),
        ];
    }

    getAppDisplayNameFromId (appId) {
        const platformLabel = this.getAppGroupLabelByAggId(appId);
        if (platformLabel) {
            return platformLabel;
        }

        const app = getActiveApplicationsMap()[appId];

        if(!app) return '';

        return app.displayName;
    }

    displayNameSorter (a, b) {
        const aDisplayName = a.displayName ? a.displayName.toUpperCase() : a.name.toUpperCase();
        const bDisplayName = b.displayName ? b.displayName.toUpperCase() : b.name.toUpperCase();
        if (aDisplayName < bDisplayName) {
            return -1;
        }
        if (aDisplayName > bDisplayName) {
            return 1;
        }

        return 0;
    }

    isDeactivatedApp (app) {
        return get(app, 'markedForDeletionAt', 0) > 0;
    }

    isLegacyApp (app) {
        return app.id === this.legacyAppId;
    }

    meetsMinimumVersion (semver) {
        return Promise.resolve(meetsMinimumVersion(semver));
    }

    isMobile (app) {
        const platform = get(app, 'platform', 'web');
        const mobilePlatforms = [this.IOS_PLATFORM, this.ANDROID_PLATFORM, this.MOBILE_PLATFORM];

        return mobilePlatforms.includes(platform);
    }

    isExtension (app) {
        const platform = get(app, 'platform');

        return platform === 'extension';
    }

    getLowestSdkVersionObj (versionData) {
        return versionData.reduce((lowest, current) => {
            if (semver.lt(semver.coerce(current.sdkVersion), semver.coerce(lowest.sdkVersion))) {
                return current;
            }

            return lowest;
        });
    }

    showMinAppVersionMessage (minSdkVersion = '0.0.0', versionData = []) {
        // if there is no versionData info - do not show the message
        // If the guide's minSdkVersion is lower than the minimum active SDK version - Do not show any warning message
        if (
            versionData === null ||
            !versionData?.length ||
            semver.lt(semver.coerce(minSdkVersion), MIN_ACTIVE_SDK_VERSION)
        ) {
            return false;
        }

        // If the lowest SDK version using the app is lower than the guide's minSdkVersion, show the warning message
        const lowestSdkVersionObj = this.getLowestSdkVersionObj(versionData);
        const islowestSdkVersionLowerThanGuideMinSdkVersion =
            lowestSdkVersionObj &&
            semver.lt(semver.coerce(lowestSdkVersionObj.sdkVersion), semver.coerce(minSdkVersion));

        return islowestSdkVersionLowerThanGuideMinSdkVersion;
    }
}

export const AppsService = new Apps();
